import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { subscribeTick } from '@/lib/dots/ticker'
import { createRandom } from '@/lib/dots/noise'
import { readDotColors } from '@/lib/dots/palette'

import {
    clamp01,
    paintDots,
    toLevel
} from '@/lib/dots/paint'

interface HalftoneImageProps {
    src: string
    alt: string
    className?: string
    cell?: number
    mobileCell?: number
    focusX?: number
    focusY?: number
    sizes?: string
}

const REVEAL_SECONDS = 1.6

// Renders a photo as an accent-colored dot-matrix halftone that builds itself in when scrolled into view.
// Hovering reveals the original photo underneath.
export const HalftoneImage: FC<HalftoneImageProps> = ({
    src,
    alt,
    className,
    cell = 6,
    mobileCell = 5,
    focusX = 0.5,
    focusY = 0.5,
    sizes = '(min-width: 1024px) 50vw, 100vw'
}): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [ready, setReady] = useState(false)

    useEffect(() => {
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context) return

        const ctx = context
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const colors = readDotColors()
        const image = new window.Image()

        let width = 0
        let height = 0
        let cellPx = 0
        let cols = 0
        let rows = 0
        let tones = new Float32Array(0)
        let order = new Float32Array(0)
        let levels = new Uint8Array(0)
        let progress = reduceMotion ? 1 : 0
        let loaded = false
        let requested = false
        let inView = false
        let painted = false
        let last = 0
        let resizeFrame = 0
        let unsubscribe: (() => void) | null = null

        const sample = (): void => {
            const count = cols * rows
            const random = createRandom(cols * 31 + rows)

            tones = new Float32Array(count)
            order = new Float32Array(count)
            levels = new Uint8Array(count)

            for (let row = 0, index = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++, index++) {
                    order[index] = ((col / cols) * 0.55 + (row / rows) * 0.45) * 0.9 + random() * 0.1
                }
            }

            const sourceWidth = image.naturalWidth
            const sourceHeight = image.naturalHeight

            if (!sourceWidth || !sourceHeight) return

            const target = width / height

            let cropWidth = sourceWidth
            let cropHeight = sourceHeight

            if (sourceWidth / sourceHeight > target) cropWidth = sourceHeight * target
            else cropHeight = sourceWidth / target

            const cropX = (sourceWidth - cropWidth) * focusX
            const cropY = (sourceHeight - cropHeight) * focusY

            // Downscale in two steps so every browser averages the photo instead of skipping pixels.
            const step = document.createElement('canvas')
            step.width = cols * 4
            step.height = rows * 4

            const stepCtx = step.getContext('2d')
            const buffer = document.createElement('canvas')
            buffer.width = cols
            buffer.height = rows

            const bufferCtx = buffer.getContext('2d', { willReadFrequently: true })

            if (!stepCtx || !bufferCtx) return

            const gridWidth = width / cellPx
            const gridHeight = height / cellPx

            stepCtx.fillStyle = '#ffffff'
            stepCtx.fillRect(0, 0, step.width, step.height)
            stepCtx.imageSmoothingEnabled = true
            stepCtx.imageSmoothingQuality = 'high'
            stepCtx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, gridWidth * 4, gridHeight * 4)

            bufferCtx.imageSmoothingEnabled = true
            bufferCtx.imageSmoothingQuality = 'high'
            bufferCtx.drawImage(step, 0, 0, cols, rows)

            const data = bufferCtx.getImageData(0, 0, cols, rows).data
            const darkness = new Float32Array(count)

            for (let i = 0; i < count; i++) {
                const offset = i * 4
                darkness[i] = 1 - (0.2126 * data[offset] + 0.7152 * data[offset + 1] + 0.0722 * data[offset + 2]) / 255
            }

            const sorted = Float32Array.from(darkness).sort()
            const low = sorted[Math.floor(count * 0.03)]
            const high = sorted[Math.min(count - 1, Math.floor(count * 0.985))]
            const range = Math.max(0.12, high - low)

            for (let i = 0; i < count; i++) {
                tones[i] = Math.pow(clamp01((darkness[i] - low) / range), 0.9)
            }
        }

        const draw = (): void => {
            if (!cols || !rows || tones.length !== cols * rows) return

            const animating = progress < 1
            const front = progress * 1.25

            for (let row = 0, index = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++, index++) {
                    let value = tones[index]

                    if (animating) value *= clamp01((front - order[index]) / 0.25)

                    levels[index] = toLevel(value, col, row)
                }
            }

            ctx.clearRect(0, 0, width, height)
            paintDots(ctx, levels, cols, rows, cellPx, colors)

            if (!painted) {
                painted = true
                setReady(true)
            }
        }

        const resize = (): void => {
            const rect = canvas.getBoundingClientRect()
            const cssWidth = Math.max(1, rect.width)
            const cssHeight = Math.max(1, rect.height)
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            const cssCell = cssWidth < 640 ? mobileCell : cell

            cellPx = Math.max(2, Math.round(cssCell * dpr))
            width = Math.round(cssWidth * dpr)
            height = Math.round(cssHeight * dpr)
            canvas.width = width
            canvas.height = height
            cols = Math.ceil(width / cellPx)
            rows = Math.ceil(height / cellPx)

            if (!loaded) return

            sample()
            draw()
        }

        const stop = (): void => {
            unsubscribe?.()
            unsubscribe = null
        }

        const tick = (now: number): void => {
            const delta = last ? Math.min((now - last) / 1000, 0.05) : 1 / 60

            last = now
            progress = Math.min(1, progress + delta / REVEAL_SECONDS)
            draw()

            if (progress >= 1) stop()
        }

        const startReveal = (): void => {
            if (unsubscribe || progress >= 1 || !loaded || !inView) return

            last = 0
            unsubscribe = subscribeTick(tick)
        }

        image.decoding = 'async'
        image.onload = () => {
            loaded = true
            resize()
            startReveal()
        }

        const loader = new IntersectionObserver(entries => {
            if (!entries[0]?.isIntersecting || requested) return

            requested = true
            image.src = src
            loader.disconnect()
        }, { rootMargin: '600px 0px' })

        const revealer = new IntersectionObserver(entries => {
            if (!entries[0]?.isIntersecting) return

            inView = true
            startReveal()
            revealer.disconnect()
        }, { rootMargin: '0px 0px -12% 0px' })

        const resizer = new ResizeObserver(() => {
            cancelAnimationFrame(resizeFrame)
            resizeFrame = requestAnimationFrame(resize)
        })

        resize()
        loader.observe(canvas)
        revealer.observe(canvas)
        resizer.observe(canvas)

        return () => {
            stop()
            loader.disconnect()
            revealer.disconnect()
            resizer.disconnect()
            cancelAnimationFrame(resizeFrame)
            image.onload = null
        }
    }, [src, cell, mobileCell, focusX, focusY])

    return (
        <div className={cn('group/halftone relative overflow-hidden', className)}>
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                className='halftone-photo object-cover opacity-0 transition-opacity duration-700 ease-out group-hover/halftone:opacity-100'
                style={{ objectPosition: `${focusX * 100}% ${focusY * 100}%` }}
            />

            <canvas
                ref={canvasRef}
                aria-hidden='true'
                className={cn(
                    'absolute inset-0 block size-full transition-opacity duration-700 ease-out group-hover/halftone:opacity-0',
                    ready ? 'opacity-100' : 'opacity-0'
                )}
            />
        </div>
    )
}
